import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { slugify } from "../utils/stringUtils";
import {
  Box,
  Typography,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import IntegrationsNav, { IntegrationsNavValue } from "../components/IntegrationsNav";
import IntegrationsActive from "../components/integrations/IntegrationsActive";
import IntegrationsDirectory from "../components/integrations/IntegrationsDirectory";
import { ActiveConnection, DirectoryEntry } from "../components/integrations/integrationsModel";
import {
  Tab,
  Tabs,
} from "@mui/material";
import AvailableIntegrationCard from "../components/AvailableIntegrationCard";
import { useIntegration, usePlatforms } from "hooks/useIntegration";
import { Customer, Application, Platform } from "types/integration";
import GlobalLoadingOverlay from "../components/GlobalLoadingOverlay";
import { useUserMsp } from "../hooks/useUserMsp";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import IntegrationApi from "../apis/integrationApi";
import IntegrationCard from "../components/IntegrationCard";
import AddIntegrationModal, { IntegrationCustomerOption } from "../components/AddIntegrationModal";
import TeamsBotOnboarding from "../components/TeamsBotOnboarding";
import { NeoButton } from "../shared/components";
import {
  COMING_SOON_INTEGRATIONS,
  INTEGRATION_CATALOG,
  INTEGRATION_CATEGORIES,
  IntegrationCatalogEntry,
  IntegrationCategoryId,
  filterOutConnectedEntries,
  formatAppName,
  isIntegrationEnabled,
  getCategoryForApplicationName,
  getCategoryForPlatformType,
  getConnectorDescription,
  getConnectorLogo,
  matchesIntegrationSearch,
  normalizeIntegrationName,
} from "../utils/connectorUtils";
import { isMspOrgOrHiddenCustomer } from "../utils/mspOrgCustomer";
import { isMspOrgIdentityLdEnabled } from "../utils/mspOrgIdentityAccess";
import { isTeamsTeammateLdEnabled } from "../utils/teamsTeammateAccess";
import { usePublishConfigurationSupport } from "../configurationSupport/ConfigurationSupportContext";
import { NEUTRAL } from "../styles/brandTokens";

const integrationApi = new IntegrationApi();

const ALL_CATALOG_KEYS = new Set(INTEGRATION_CATALOG.map((entry) => entry.key));

const platformCatalogKey = (type: string, name: string): string =>
  `platform:${String(type || "").toLowerCase()}:${normalizeIntegrationName(name)}`;

const InlineLoadingState: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      minHeight: 220,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.5,
      color: "#666F8D",
      fontFamily: "var(--theo-font-sans)",
    }}
  >
    <CircularProgress size={22} sx={{ color: "#705D4A" }} />
    <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 15, fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

interface AddModalState {
  open: boolean;
  mode: "platform" | "application";
  customerId: string;
  customerName: string;
  initialPlatformName?: string;
  initialPlatformType?: string;
  initialApplicationName?: string;
}

const CLOSED_ADD_MODAL: AddModalState = {
  open: false,
  mode: "platform",
  customerId: "0",
  customerName: "",
};

type IntegrationsView = "teams" | "active" | "directory";

const Integrations: React.FC = () => {
  const [categoryTab, setCategoryTab] = useState<IntegrationsNavValue>("all");
  /**
   * Active = what is live right now; Directory = what Theo could connect to.
   * `null` until the user picks a tab, so the first view is Active.
   */
  const [view, setView] = useState<IntegrationsView | null>(null);
  /** Prefills the Active search when arriving from a Directory "N connected" chip. */
  const [activeSearch, setActiveSearch] = useState("");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [connectedOnly, setConnectedOnly] = useState(false);
  const [addModal, setAddModal] = useState<AddModalState>(CLOSED_ADD_MODAL);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [chooserCustomerId, setChooserCustomerId] = useState<string>("");
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const flags = useFeatureFlag();
  const mspOrgIdentityEnabled = isMspOrgIdentityLdEnabled(flags as Record<string, unknown>);
  const teamsTeammateEnabled = isTeamsTeammateLdEnabled(flags as Record<string, unknown>);
  const [mspOrgCustomer, setMspOrgCustomer] = useState<Customer | null>(null);
  const [mspOrgError, setMspOrgError] = useState("");

  const { customers, customersLoading, customersLoaded, customersError, getCustomers } =
    useIntegration();
  const {
    platforms,
    platformsLoading,
    platformsLoaded,
    platformsError,
    getPlatforms,
    updatePlatformState,
  } = usePlatforms();
  const { userMspInfo, isLoading: userMspLoading, error: userMspError } = useUserMsp();

  const [appsByCustomer, setAppsByCustomer] = useState<Record<string, Application[]>>({});
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsError, setAppsError] = useState("");
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  useEffect(() => {
    if (!mspOrgIdentityEnabled || !userMspInfo?.msp_id) {
      setMspOrgCustomer(null);
      return;
    }
    let cancelled = false;
    setMspOrgError("");
    integrationApi
      .getMspOrgCustomer()
      .then((row) => {
        if (!cancelled) setMspOrgCustomer(row as Customer);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setMspOrgError(err?.message || "Unable to load your organization integration.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [mspOrgIdentityEnabled, userMspInfo?.msp_id]);

  const customerRows = useMemo(() => {
    const rawCustomers = customers as any;
    return (Array.isArray(rawCustomers) ? rawCustomers : rawCustomers?.rows || []) as Customer[];
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    if (!userMspInfo?.msp_id) return [];
    return customerRows.filter(
      (c: Customer) =>
        String((c as any).msp_id) === String(userMspInfo?.msp_id) && !isMspOrgOrHiddenCustomer(c)
    );
  }, [customerRows, userMspInfo?.msp_id]);

  const integrationCustomers = useMemo<IntegrationCustomerOption[]>(
    () => [
      ...(mspOrgCustomer
        ? [{ id: String(mspOrgCustomer.id), name: mspOrgCustomer.name, detail: "your organization" }]
        : []),
      ...filteredCustomers.map((customer) => ({
        id: String(customer.id),
        name: customer.name,
      })),
    ],
    [filteredCustomers, mspOrgCustomer]
  );

  const loadApps = useCallback(async () => {
    const appCustomers = [...filteredCustomers, ...(mspOrgCustomer ? [mspOrgCustomer] : [])];
    if (appCustomers.length === 0) {
      setAppsByCustomer({});
      return;
    }
    setAppsLoading(true);
    setAppsError("");
    try {
      const entries = await Promise.all(
        appCustomers.map(async (customer) => {
          const customerId = String(customer.id);
          try {
            const response = (await integrationApi.getApplicationsByCustomer(customerId)) as
              | { rows?: Application[] }
              | Application[];
            const rows = Array.isArray(response) ? response : response?.rows ?? [];
            return [customerId, rows] as const;
          } catch {
            return [customerId, []] as const;
          }
        })
      );
      setAppsByCustomer(Object.fromEntries(entries));
    } catch (err: any) {
      setAppsError(err?.message || "Unable to load application integrations.");
    } finally {
      setAppsLoading(false);
    }
  }, [filteredCustomers, mspOrgCustomer]);

  useEffect(() => {
    if (customersLoaded) loadApps();
  }, [customersLoaded, loadApps]);

  useEffect(() => {
    if (!chooserCustomerId && filteredCustomers.length > 0) {
      setChooserCustomerId(String(filteredCustomers[0].id));
    }
  }, [chooserCustomerId, filteredCustomers]);

  const refreshAll = useCallback(async () => {
    if (userMspInfo?.msp_id) getPlatforms(String(userMspInfo.msp_id));
    await loadApps();
  }, [getPlatforms, loadApps, userMspInfo?.msp_id]);

  const platformRows = useMemo(() => {
    const raw = platforms as any;
    const rows = (Array.isArray(raw) ? raw : raw?.rows || []) as (Platform & {
      enabled?: boolean;
    })[];
    return rows;
  }, [platforms]);

  const platformsByCategory = useMemo(() => {
    const grouped = Object.fromEntries(
      INTEGRATION_CATEGORIES.map((category) => [category.id, []])
    ) as unknown as Record<IntegrationCategoryId, typeof platformRows>;
    platformRows.forEach((platform) => {
      const category = getCategoryForPlatformType((platform as Platform).type);
      grouped[category].push(platform);
    });
    return grouped;
  }, [platformRows]);

  const connectedPlatformKeys = useMemo(() => {
    const keys = new Set<string>();
    platformRows.forEach((platform) => {
      keys.add(platformCatalogKey(String((platform as Platform).type || ""), platform.name));
    });
    return keys;
  }, [platformRows]);

  const allConnectedApps = useMemo(() => {
    const list: Array<{ customer: Customer; application: Application }> = [];
    [...filteredCustomers, ...(mspOrgCustomer ? [mspOrgCustomer] : [])].forEach((customer) => {
      const apps = appsByCustomer[String(customer.id)] || [];
      apps.forEach((application) => list.push({ customer, application }));
    });
    return list;
  }, [appsByCustomer, filteredCustomers, mspOrgCustomer]);

  const appsByCategory = useMemo(() => {
    const grouped = Object.fromEntries(
      INTEGRATION_CATEGORIES.map((category) => [category.id, []])
    ) as unknown as Record<IntegrationCategoryId, typeof allConnectedApps>;
    allConnectedApps.forEach((entry) => {
      grouped[getCategoryForApplicationName(entry.application.name)].push(entry);
    });
    return grouped;
  }, [allConnectedApps]);

  /**
   * Both new views are projections of data already loaded: MSP-level platform
   * rows and per-customer application rows. Nothing here needs a schema change.
   */
  const activeConnections = useMemo<ActiveConnection[]>(() => {
    const mspLevel: ActiveConnection[] = platformRows.map((platform) => {
      const row = platform as Platform & { enabled?: boolean };
      return {
        key: `msp:${row.id}`,
        name: formatAppName(row.name),
        description: getConnectorDescription(row.name),
        logo: getConnectorLogo(row.name),
        category: getCategoryForPlatformType(row.type),
        scope: "msp",
        // A platform row exists only once connected; `enabled` may be absent on
        // older rows, so treat "present but unflagged" as connected.
        enabled: row.enabled === undefined ? true : isIntegrationEnabled(row),
        platform: row,
        togglingKey: `platform:${row.id}`,
      };
    });
    const perCustomer: ActiveConnection[] = allConnectedApps.map(({ customer, application }) => ({
      key: `customer:${customer.id}:${application.id}`,
      name: formatAppName(application.name),
      description: getConnectorDescription(application.name),
      logo: getConnectorLogo(application.name),
      category: getCategoryForApplicationName(application.name),
      scope: "customer",
      enabled: isIntegrationEnabled(application),
      customer,
      application,
      togglingKey: `app:${customer.id}:${application.id}`,
    }));
    return [...mspLevel, ...perCustomer];
  }, [platformRows, allConnectedApps]);

  const directoryEntries = useMemo<DirectoryEntry[]>(() => {
    const connectedAppNames = new Map<string, number>();
    allConnectedApps.forEach(({ application }) => {
      const key = normalizeIntegrationName(application.name);
      connectedAppNames.set(key, (connectedAppNames.get(key) || 0) + 1);
    });
    const available: DirectoryEntry[] = INTEGRATION_CATALOG.map((entry) => ({
      key: entry.key,
      name: formatAppName(entry.name),
      description: entry.blurb,
      logo: getConnectorLogo(entry.name),
      category: entry.category,
      scope: entry.scope === "platform" ? "msp" : "customer",
      connectedCount:
        entry.scope === "platform"
          ? connectedPlatformKeys.has(platformCatalogKey(String(entry.platformType || ""), entry.name))
            ? 1
            : 0
          : connectedAppNames.get(normalizeIntegrationName(entry.name)) || 0,
    }));
    // Roadmap entries go last so connectable integrations lead every category.
    const comingSoon: DirectoryEntry[] = COMING_SOON_INTEGRATIONS.map((entry) => ({
      key: entry.key,
      name: entry.name,
      description: entry.blurb,
      logo: entry.logo,
      category: entry.category,
      scope: "msp",
      connectedCount: 0,
      comingSoon: true,
    }));
    return [...available, ...comingSoon];
  }, [allConnectedApps, connectedPlatformKeys]);

  /** Directory card -> the existing add flow, keyed back to its catalog entry. */
  /** The switch, and only the switch, changes enablement. */
  const handleActiveToggle = useCallback(
    (connection: ActiveConnection) => {
      if (connection.scope === "msp" && connection.platform) {
        handlePlatformToggle(connection.platform);
      } else if (connection.customer && connection.application) {
        handleApplicationToggle(String(connection.customer.id), connection.application);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleDirectoryShowConnected = useCallback((entry: DirectoryEntry) => {
    setActiveSearch(entry.name);
    setView("active");
  }, []);

  const handleDirectoryConnect = useCallback(
    (entry: DirectoryEntry) => {
      if (entry.comingSoon) return;
      const catalogEntry = INTEGRATION_CATALOG.find((candidate) => candidate.key === entry.key);
      if (catalogEntry) {
        openAddForCatalogEntry(catalogEntry);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chooserCustomerId, filteredCustomers, mspOrgCustomer]
  );

  /**
   * A row click opens that connection's configuration - the chevron promises
   * navigation. It must never toggle: an earlier version called the toggle
   * handler here, so clicking a row to inspect it silently disabled the
   * integration. Enabling/disabling lives on the configuration screen.
   */
  const handleActiveOpen = useCallback(
    (connection: ActiveConnection) => {
      if (connection.scope === "msp" && connection.platform) {
        const platformId = connection.platform.id;
        const identifier = platformId
          ? `${platformId}-${slugify(connection.platform.name)}`
          : slugify(connection.platform.name);
        navigate(`/platform-configuration/0/${identifier}`);
        return;
      }
      if (connection.customer && connection.application) {
        navigate(
          `/app-configuration/${connection.customer.id}/${slugify(connection.application.name)}`
        );
      }
    },
    [navigate]
  );

  const connectedCounts = useMemo<Partial<Record<IntegrationCategoryId, number>>>(() => {
    const counts: Partial<Record<IntegrationCategoryId, number>> = {};
    (Object.keys(platformsByCategory) as IntegrationCategoryId[]).forEach((category) => {
      const platformCount = (platformsByCategory[category] || []).filter((platform) =>
        isIntegrationEnabled(platform as { enabled?: unknown })
      ).length;
      const appCount = (appsByCategory[category] || []).filter(({ application }) =>
        isIntegrationEnabled(application as { enabled?: unknown })
      ).length;
      counts[category] = platformCount + appCount;
    });
    return counts;
  }, [appsByCategory, platformsByCategory]);

  const availableByCategory = useMemo(() => {
    const grouped = Object.fromEntries(
      INTEGRATION_CATEGORIES.map((category) => [category.id, []])
    ) as unknown as Record<IntegrationCategoryId, IntegrationCatalogEntry[]>;
    INTEGRATION_CATALOG.forEach((entry) => {
      if (!ALL_CATALOG_KEYS.has(entry.key)) return;
      if (entry.scope === "platform") {
        if (connectedPlatformKeys.has(entry.key)) return;
        grouped[entry.category].push(entry);
      } else {
        grouped[entry.category].push(entry);
      }
    });
    return grouped;
  }, [connectedPlatformKeys]);

  const availableCounts = useMemo<Partial<Record<IntegrationCategoryId, number>>>(() => {
    const counts: Partial<Record<IntegrationCategoryId, number>> = {};
    (Object.keys(availableByCategory) as IntegrationCategoryId[]).forEach((category) => {
      counts[category] = (availableByCategory[category] || []).length;
    });
    return counts;
  }, [availableByCategory]);


  const showAlert = (message: string, severity: "success" | "error") =>
    setAlert({ open: true, message, severity });

  const handlePlatformToggle = async (platform: Platform & { enabled?: boolean }) => {
    const key = `platform:${platform.id}`;
    setTogglingKey(key);
    try {
      await updatePlatformState(String(platform.id), !Boolean(platform.enabled));
      if (String((platform as Platform).type || "").toLowerCase() === "psa") {
        window.location.reload();
        return;
      }
      showAlert(
        `${formatAppName(platform.name)} has been ${platform.enabled ? "disabled" : "enabled"} successfully.`,
        "success"
      );
      if (userMspInfo?.msp_id) getPlatforms(String(userMspInfo.msp_id));
    } catch (err: any) {
      showAlert(err?.message || `Failed to update ${formatAppName(platform.name)}.`, "error");
    } finally {
      setTogglingKey(null);
    }
  };

  const handleApplicationToggle = async (customerId: string, application: Application) => {
    const key = `app:${customerId}:${application.id}`;
    setTogglingKey(key);
    try {
      await integrationApi.updateApplicationState(customerId, application.id, !Boolean(application.enabled));
      showAlert(
        `${formatAppName(application.name)} has been ${
          application.enabled ? "disabled" : "enabled"
        } successfully.`,
        "success"
      );
      await loadApps();
    } catch (err: any) {
      showAlert(err?.message || "Failed to update application state.", "error");
    } finally {
      setTogglingKey(null);
    }
  };

  const openAddForCatalogEntry = (entry: IntegrationCatalogEntry, customerId?: string) => {
    if (entry.scope === "platform") {
      setAddModal({
        open: true,
        mode: "platform",
        customerId: "0",
        customerName: "",
        initialPlatformName: entry.name,
        initialPlatformType: entry.platformType,
      });
    } else {
      const targetCustomerId =
        customerId || chooserCustomerId || (filteredCustomers[0] ? String(filteredCustomers[0].id) : "");
      const targetCustomer = [...filteredCustomers, ...(mspOrgCustomer ? [mspOrgCustomer] : [])].find(
        (c) => String(c.id) === String(targetCustomerId)
      );
      if (!targetCustomerId) {
        showAlert("Add a customer from the Accounts page before connecting a workplace app.", "error");
        return;
      }
      setAddModal({
        open: true,
        mode: "application",
        customerId: String(targetCustomerId),
        customerName: targetCustomer?.name || "",
        initialApplicationName: entry.name,
      });
    }
    setChooserOpen(false);
  };

  const handleAddSuccess = async () => {
    await refreshAll();
    showAlert("Integration saved successfully.", "success");
  };

  const userMspPending = userMspLoading || (!userMspInfo?.msp_id && !userMspError);
  const initialLoading =
    userMspPending || platformsLoading || !platformsLoaded || customersLoading || !customersLoaded;
  const currentView: IntegrationsView =
    view === "teams" && !teamsTeammateEnabled ? "active" : view ?? "active";
  const pageError = userMspError || customersError || platformsError || appsError || mspOrgError;

  const applySearch = <T,>(items: T[], getText: (item: T) => { name: string; description: string }): T[] => {
    if (!search.trim()) return items;
    return items.filter((item) => {
      const { name, description } = getText(item);
      return matchesIntegrationSearch(name, description, search);
    });
  };



  const renderAppCustomerBlock = (
    customer: Customer,
    categoryId: IntegrationCategoryId,
    emptyLabel = "Nothing connected for this customer yet."
  ) => {
    const customerId = String(customer.id);
    const existingApps = (appsByCustomer[customerId] || []).filter(
      (app) => getCategoryForApplicationName(app.name) === categoryId
    );
    const connected = applySearch(
      existingApps.filter((app) => isIntegrationEnabled(app)),
      (app) => ({
        name: app.name,
        description: getConnectorDescription(app.name),
      })
    );
    const disabledApps = applySearch(
      existingApps.filter((app) => !isIntegrationEnabled(app)),
      (app) => ({
        name: app.name,
        description: getConnectorDescription(app.name),
      })
    );
    const existingNames = new Set(existingApps.map((app) => normalizeIntegrationName(app.name)));
    const availableEntries = applySearch(
      filterOutConnectedEntries(
        (availableByCategory[categoryId] || []).filter((e) => e.scope === "application"),
        existingNames
      ),
      (entry) => ({ name: entry.name, description: entry.blurb })
    );
    return (
      <Box
        key={`${categoryId}:${customerId}`}
        sx={{ mt: 2, p: 2, border: "1px solid #EEF1F5", borderRadius: "6px", background: "#FAFBFC" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700, fontSize: 15, color: "#161616" }}>
            {customer.name}
          </Typography>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#666F8D" }}>
            {connected.length} connected
          </Typography>
        </Box>
        {connected.length > 0 ? (
          <Grid container spacing={1}>
            {connected.map((application) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={application.id}>
                <IntegrationCard
                  mode="application"
                  logo={getConnectorLogo(application.name)}
                  name={application.name}
                  description={getConnectorDescription(application.name)}
                  enabled={Boolean(application.enabled)}
                  onToggle={() => handleApplicationToggle(customerId, application)}
                  isConfigured={Boolean(application.enabled)}
                  selectedCustomerId={customerId}
                  loading={togglingKey === `app:${customerId}:${application.id}`}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#919191", fontStyle: "italic", fontSize: 14 }}>
            {emptyLabel}
          </Typography>
        )}
        {!connectedOnly && (disabledApps.length > 0 || availableEntries.length > 0) ? (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, fontWeight: 700, color: "#666F8D", mb: 1 }}>
              AVAILABLE TO CONNECT
            </Typography>
            <Grid container spacing={1}>
              {disabledApps.map((application) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`disabled-${application.id}`}>
                  <AvailableIntegrationCard
                    logo={getConnectorLogo(application.name)}
                    name={application.name}
                    description={getConnectorDescription(application.name)}
                    connectLabel="Enable"
                    onConnect={() => handleApplicationToggle(customerId, application)}
                  />
                </Grid>
              ))}
              {availableEntries.map((entry) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`${customerId}:${entry.key}`}>
                  <AvailableIntegrationCard
                    logo={getConnectorLogo(entry.name)}
                    name={entry.name}
                    description={entry.blurb}
                    onConnect={() => openAddForCatalogEntry(entry, customerId)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderWorkplaceCustomerBlock = (customer: Customer) =>
    renderAppCustomerBlock(customer, "workplace");

  const renderAppCategoryCustomerBlocks = (
    categoryId: IntegrationCategoryId,
    emptyCustomersLabel: string
  ) => {
    const appCustomers = [...filteredCustomers, ...(mspOrgCustomer ? [mspOrgCustomer] : [])];
    const searchedCustomers = search.trim()
      ? appCustomers.filter((customer) => {
          const apps = (appsByCustomer[String(customer.id)] || []).filter(
            (app) => getCategoryForApplicationName(app.name) === categoryId
          );
          if (customer.name.toLowerCase().includes(search.trim().toLowerCase())) return true;
          return apps.some((app) =>
            matchesIntegrationSearch(app.name, getConnectorDescription(app.name), search)
          );
        })
      : appCustomers;
    if (searchedCustomers.length === 0) {
      return (
        <Alert severity="info" sx={{ fontFamily: "var(--theo-font-sans)", mt: 1 }}>
          {emptyCustomersLabel}
        </Alert>
      );
    }
    return (
      <>
        {searchedCustomers.map((customer) =>
          renderAppCustomerBlock(
            customer,
            categoryId,
            categoryId === "passwords" ? "No vaults connected for this customer yet." : undefined
          )
        )}
      </>
    );
  };

  const chooserConnectedAppNames = useMemo(() => {
    if (!chooserCustomerId) return new Set<string>();
    return new Set(
      (appsByCustomer[chooserCustomerId] || []).map((app) => normalizeIntegrationName(app.name))
    );
  }, [appsByCustomer, chooserCustomerId]);

  const chooserVisibleCatalog = useMemo(
    () =>
      INTEGRATION_CATALOG.filter((entry) => {
        if (entry.scope === "platform") return !connectedPlatformKeys.has(entry.key);
        return !chooserConnectedAppNames.has(normalizeIntegrationName(entry.name));
      }),
    [chooserConnectedAppNames, connectedPlatformKeys]
  );

  const supportSnapshot = useMemo(
    () => ({
      surface: "integrations" as const,
      title: "Integrations",
      configuration: {
        category_tab: categoryTab,
        search,
        connected_only: connectedOnly,
        page_error: pageError ? String(pageError) : "",
        organization_error: mspOrgError || "",
        organization_tenant_loaded: Boolean(mspOrgCustomer),
        customer_count: filteredCustomers.length,
        customers: filteredCustomers.slice(0, 30).map((customer) => ({
          id: customer.id,
          name: customer.name,
        })),
        connected_platforms: platformRows.slice(0, 40).map((platform) => ({
          name: platform.name,
          type: (platform as Platform).type,
          enabled: Boolean(platform.enabled),
        })),
        connected_applications: allConnectedApps.slice(0, 40).map(({ customer, application }) => ({
          customer_name: customer.name,
          name: application.name,
          enabled: Boolean(application.enabled),
        })),
        add_dialog: addModal.open
          ? {
              mode: addModal.mode,
              customer_name: addModal.customerName,
              platform: addModal.initialPlatformName || "",
              application: addModal.initialApplicationName || "",
            }
          : null,
      },
    }),
    [
      categoryTab,
      search,
      connectedOnly,
      pageError,
      mspOrgError,
      mspOrgCustomer,
      filteredCustomers,
      platformRows,
      allConnectedApps,
      addModal,
    ]
  );
  usePublishConfigurationSupport("integrations-page", supportSnapshot, 10);

  return (
    <Box width="100%" sx={{ p: 1, background: NEUTRAL.page, height: "100%", overflowY: "auto" }}>
      <Box
        sx={{
          background: "#FFF",
          borderRadius: "8px",
          boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.05)",
          border: "1px solid #EEF1F5",
          p: 2.5,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 240, flex: 1 }}>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 24, fontWeight: 700, color: "#161616" }}>
              Integrations
            </Typography>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#666F8D", fontSize: 14, mt: 0.5 }}>
              Connect PSA ticketing, RMM, documentation, workplace, chat, licenses, and vaults so
              Theo can act on live data. MSP-level platforms serve every customer; workplace apps,
              chat, and vaults connect per customer.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <NeoButton variant="neo" startIcon={<AddIcon />} onClick={() => setChooserOpen(true)} sx={{ fontWeight: 600 }}>
              Add integration
            </NeoButton>
          </Box>
        </Box>
      </Box>

      {/* Active = live connections, grouped by who they serve. Directory = the
          catalogue of everything connectable. The old flat category list mixed
          the two, so "what is connected for this customer" had no single view. */}
      <Tabs
        value={currentView}
        onChange={(_event, next) => {
          if (next === "active") setActiveSearch("");
          setView(next as IntegrationsView);
        }}
        sx={{ mb: 2, borderBottom: `1px solid ${NEUTRAL.border}`, minHeight: 40 }}
      >
        {teamsTeammateEnabled ? (
          <Tab
            value="teams"
            label="Theo Teams"
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 40 }}
          />
        ) : null}
        <Tab
          value="active"
          label={`Active${activeConnections.length ? `  ${activeConnections.length}` : ""}`}
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 40 }}
        />
        <Tab
          value="directory"
          label="Directory"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 40 }}
        />
      </Tabs>

      {pageError && !initialLoading ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {String(pageError)}
        </Alert>
      ) : null}

      {currentView === "teams" && teamsTeammateEnabled ? (
        <Box sx={{ pb: 4 }}>
          <TeamsBotOnboarding />
        </Box>
      ) : initialLoading || appsLoading ? (
        <InlineLoadingState label="Loading integrations..." />
      ) : (
        <Box sx={{ pb: 4 }}>
          {currentView === "active" ? (
            <IntegrationsActive
              key={activeSearch}
              initialSearch={activeSearch}
              connections={activeConnections}
              onOpen={handleActiveOpen}
              onToggle={handleActiveToggle}
              togglingKey={togglingKey}
            />
          ) : (
            <IntegrationsDirectory
              entries={directoryEntries}
              onConnect={handleDirectoryConnect}
              onShowConnected={handleDirectoryShowConnected}
            />
          )}
        </Box>
      )}

      {(togglingKey) && <GlobalLoadingOverlay />}

      <Dialog open={chooserOpen} onClose={() => setChooserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>
          Add an integration
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#666F8D", mb: 1.5 }}>
            Pick what to connect. Workplace apps, chat, and vaults connect per customer — pick the
            customer first.
          </Typography>
          {(filteredCustomers.length > 0 || mspOrgCustomer) && (
            <LayeredAutocompleteSelect
              label="Customer (for per-customer apps)"
              value={chooserCustomerId}
              onChange={value => setChooserCustomerId(String(value))}
              size="small"
              fullWidth
              sx={{ ...({ mb: 1.5 }), ...({ fontFamily: "var(--theo-font-sans)" }) }}
              disableClearable
              options={[...(mspOrgCustomer ? [{ value: String(mspOrgCustomer.id), label: mspOrgCustomer.name + " (your organization)" }] : []), ...filteredCustomers.map(customer => ({ value: String(customer.id), label: customer.name }))]}
            />
          )}
          <List>
            {INTEGRATION_CATEGORIES.map((category) => {
              const entries = chooserVisibleCatalog.filter((entry) => entry.category === category.id);
              if (entries.length === 0) return null;
              return (
              <Box key={category.id}>
                <Typography
                  sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, fontWeight: 700, color: "#666F8D", mt: 1 }}
                >
                  {category.title.toUpperCase()} · {category.scopeLabel.toUpperCase()}
                </Typography>
                {entries.map((entry) => (
                  <ListItemButton
                    key={entry.key}
                    onClick={() =>
                      openAddForCatalogEntry(
                        entry,
                        entry.scope === "application" ? chooserCustomerId : undefined
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={getConnectorLogo(entry.name)}
                        alt={entry.name}
                        variant="rounded"
                        sx={{ width: 40, height: 40, bgcolor: "#F5F5F5", p: 0.5 }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={formatAppName(entry.name)}
                      secondary={entry.blurb}
                      primaryTypographyProps={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}
                      secondaryTypographyProps={{ fontFamily: "var(--theo-font-sans)", fontSize: 12 }}
                    />
                  </ListItemButton>
                ))}
              </Box>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>

      <AddIntegrationModal
        open={addModal.open}
        mode={addModal.mode}
        customerId={addModal.customerId}
        customerName={addModal.customerName}
        customers={addModal.mode === "application" ? integrationCustomers : undefined}
        initialPlatformName={addModal.initialPlatformName}
        initialPlatformType={addModal.initialPlatformType}
        initialApplicationName={addModal.initialApplicationName}
        onClose={() => setAddModal(CLOSED_ADD_MODAL)}
        onSuccess={handleAddSuccess}
      />

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setAlert((prev) => ({ ...prev, open: false }))} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Integrations;
